import { Observable, throwError, of, Subject } from 'rxjs';
import { tap, catchError, switchMap, finalize } from 'rxjs/operators';
import { R } from '@core/utils/r';

export interface MemoizeOptions {
  refresh?: boolean;
  timeout?: number /* in seconds */;
}

interface MemoizeConfig<P extends Record<string, any> = any> {
  key: string;
  params?: P;
  paramKeys?: { [K in keyof P]?: (value: NonNullable<P[K]>) => string | null | undefined };
  options?: MemoizeOptions;
}

interface MemoizeItem<D = any> {
  value?: D;
  fetching: boolean;
  fetched?: number;
}

const CACHE = new Map<string, MemoizeItem>();
const QUEUE = new Map<string, Subject<any>[]>();

const DEFAULT_OPTIONS: MemoizeOptions = {
  timeout: 5 * 60, // 5 mins
};

export function memoizeclear() {
  CACHE.clear();
  QUEUE.clear();
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function memoize<T, P extends Record<string, any> = {}>(config: MemoizeConfig<P>) {
  return (source$: Observable<T>): Observable<T> => {
    return of(null).pipe(
      switchMap(() => {
        const memoized$ = getMemoizeItem<T>(config);

        if (memoized$) return memoized$;

        return of(null).pipe(
          tap(() => {
            handleMemoizeStart(config);
          }),
          switchMap(() => source$),
          tap(value => {
            handleMemoizeResult(config, value);
          }),
          catchError(error => {
            handleMemoizeError(config, error);

            return throwError(error);
          }),
          finalize(() => {
            handleMemoizeEnd(config);
          }),
        );
      }),
    );
  };
}

// eslint-disable-next-line @typescript-eslint/ban-types
export function memoizeClearKey<T, P extends Record<string, any> = {}>(config: MemoizeConfig<P>) {
  return (source$: Observable<T>): Observable<T> => {
    return of(null).pipe(
      tap(() => {
        clearMemoizeItem(config);
      }),
      switchMap(() => source$),
    );
  };
}

function getMemoizeItem<T>(config: MemoizeConfig): Observable<T> | null {
  const key = getMemoizeKey(config);

  const cache = CACHE.get(key);

  const options = { ...DEFAULT_OPTIONS, ...config.options };

  if (cache && cache.fetching) {
    const obs$ = new Subject<T>();

    pushToQueue(key, obs$);

    return obs$;
  }

  if (options.refresh === true || !cache || !cache.fetched || Date.now() - cache.fetched > (options.timeout || 0) * 1000) {
    return null;
  }

  return of(cache.value);
}

function clearMemoizeItem(config: MemoizeConfig) {
  const key = getMemoizeKey(config);
  CACHE.delete(key);
}

function handleMemoizeResult(config: MemoizeConfig, value: any) {
  const key = getMemoizeKey(config);

  const cache = CACHE.get(key);

  if (cache) {
    cache.value = value;
    cache.fetched = Date.now();
  }

  flushQueue(key, { value });
}

function handleMemoizeError(config: MemoizeConfig, error: any) {
  const key = getMemoizeKey(config);

  flushQueue(key, { error: error || {} });
}

function handleMemoizeStart(config: MemoizeConfig) {
  const key = getMemoizeKey(config);

  const cache = CACHE.get(key);

  if (cache) {
    cache.fetching = true;
  } else {
    CACHE.set(key, { fetching: true });
  }
}

function handleMemoizeEnd(config: MemoizeConfig) {
  const key = getMemoizeKey(config);

  const cache = CACHE.get(key);

  if (cache) cache.fetching = false;
}

function pushToQueue(key: string, subject$: Subject<any>) {
  if (!QUEUE.has(key)) QUEUE.set(key, []);

  QUEUE.get(key)!.push(subject$);
}

function flushQueue(key: string, data: { value?: any; error?: any }) {
  const subjects = QUEUE.get(key);

  if (!subjects) return;

  R.forEach(subjects, subject$ => {
    if (data.error) {
      subject$.error(data.error);
    } else {
      subject$.next(data.value);
      subject$.complete();
    }
  });

  QUEUE.delete(key);
}

function getMemoizeKey(config: MemoizeConfig) {
  const params: [string, string][] = [];

  R.forEach(config.params, (value, key) => {
    const paramKey = getMemoizeParamKey(config, key, value);

    if (paramKey) {
      params.push([key, `${key}=${paramKey}`]);
    }
  });

  let key = config.key;

  if (params.length) {
    // sort and join params
    const paramKeys = R.sortBy(params, param => param[0])
      .map(param => param[1])
      .join(',');

    key += `:${paramKeys}`;
  }

  return key;
}

function getMemoizeParamKey(config: MemoizeConfig, key: string, value: any) {
  if (R.isNil(value)) return null;

  const getParamKey = config.paramKeys && config.paramKeys[key];
  if (getParamKey) return getParamKey(value);

  return JSON.stringify(value);
}
